import { useParams } from 'react-router-dom';
import _get from 'lodash/get';
import {
  Department,
  FilterGroupingEnum,
  Team,
  useDepartmentsSearchListQuery,
  User,
} from 'graphql-common';
import useSearchParam from '@lib/hooks/useSearchParam';
import usePaginationParams from '@lib/hooks/usePaginationParams';
import useFilterParams from '@lib/hooks/useFilterParams';
import useUsersQueryHook from 'utils/fetch/hooks/useUsersQueryHook';
import useDepartmentsQueryHook from 'utils/fetch/hooks/useDepartmentsQueryHook';
import { TeamViewMode } from 'routes/Teams/enums';
import useTeamQueryHook from 'utils/fetch/hooks/useTeamQueryHook';
import { ListQueryHookResult, SingleQueryHookResult } from 'utils/fetch/types';

export type UseTeamQueriesResult = {
  useUsersQueryHookResult: ListQueryHookResult<User>;
  useTeamQueryHookResult: SingleQueryHookResult<Team>;
  useTeamDepartmentsQueryHookResult: ListQueryHookResult<Department>;
  linkedDepartments: Department[];
};

export default function useTeamQueries({
  viewMode,
}: {
  viewMode: TeamViewMode;
}): UseTeamQueriesResult {
  const { id = '' } = useParams();
  const [searchQuery] = useSearchParam();
  const [filterParams] = useFilterParams();
  const [paginationParams] = usePaginationParams();
  const isForm =
    viewMode === TeamViewMode.Add || viewMode === TeamViewMode.Edit;

  const useUsersQueryHookResult = useUsersQueryHook({
    paginationParams,
    searchQuery,
    filterParams,
    includeFinalAccesses: true,
    includeShortData: true,
    skip: viewMode !== TeamViewMode.ViewMembers,
    skipGetAllIds: !(
      viewMode === TeamViewMode.Edit ||
      viewMode === TeamViewMode.ViewDetails ||
      viewMode === TeamViewMode.ViewMembers ||
      viewMode === TeamViewMode.ViewDepartments
    ),
    additionalFilters: [
      {
        teamId: {
          grouping: FilterGroupingEnum.And,
          predicate: { eq: id },
        },
      },
    ],
  });

  const useTeamQueryHookResult = useTeamQueryHook({
    id,
    skip: viewMode === TeamViewMode.Add,
    fetchPolicy: 'cache-and-network',
  });

  const useTeamDepartmentsQueryHookResult = useDepartmentsQueryHook({
    paginationParams,
    orderingParams: paginationParams,
    searchQuery,
    skip: !(
      viewMode === TeamViewMode.ViewDetails ||
      viewMode === TeamViewMode.ViewMembers ||
      viewMode === TeamViewMode.ViewDepartments
    ),
    additionalFilters: [
      {
        teamsId: {
          grouping: FilterGroupingEnum.And,
          predicate: { eq: id },
        },
      },
    ],
  });

  // Lightweight fetch to prefill the "Linked department(s)" select on edit —
  // the BE's `teamsId` filter only supports looking up one team's departments
  // at a time, so this can't be batched into the Teams list itself.
  const linkedDepartmentsQuery = useDepartmentsSearchListQuery({
    fetchPolicy: 'cache-and-network',
    skip: !isForm || !id,
    variables: {
      limit: 100,
      filters: [
        {
          teamsId: {
            grouping: FilterGroupingEnum.And,
            predicate: { eq: id },
          },
        },
      ],
    },
  });
  const linkedDepartments = _get(
    linkedDepartmentsQuery.data || linkedDepartmentsQuery.previousData,
    'data.collection',
    [],
  ) as Department[];

  return {
    useUsersQueryHookResult,
    useTeamQueryHookResult,
    useTeamDepartmentsQueryHookResult,
    linkedDepartments,
  };
}
